import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { useDialog } from '../../shared/hooks/useDialog';
import { useSnackbar } from '../../shared/hooks/useSnackbar';
import { updateGroupName } from '../helpers/groupsApi';
import { sanitizeInput } from '../../helpers/sanitizeInput.helper';
import {
  GROUP_FALLBACK_NAME,
  GROUP_NAME_EDIT,
  GROUP_NAME_EDIT_BLUR_CANCEL_DELAY_MS,
} from '../constants/groups.constants';
import { GROUP_NAME_MIN_LENGTH, GROUP_NAME_MAX_LENGTH } from '../constants/groupName';
import type { GroupDashboardResponse } from '../types/groups.types';

interface GroupForNameEdit {
  id: string;
  name: string;
}

interface UseGroupNameEditResult {
  displayGroupName: string;
  isEditingGroupName: boolean;
  draftGroupName: string;
  titleBlockWidth: number;
  pendingSaveRef: React.MutableRefObject<boolean>;
  onTitleLayout: (e: { nativeEvent: { layout: { width: number } } }) => void;
  handleStartEditGroupName: () => void;
  handleCancelEditGroupName: () => void;
  handleBlurGroupName: () => void;
  handleGroupNameChange: (text: string) => void;
  handleSaveGroupName: () => Promise<void>;
}

export function useGroupNameEdit(group: GroupForNameEdit | null): UseGroupNameEditResult {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { showAlert } = useDialog();
  const { showSnackbar } = useSnackbar();

  const [optimisticGroupName, setOptimisticGroupName] = useState<string | null>(null);
  const [isEditingGroupName, setIsEditingGroupName] = useState(false);
  const [draftGroupName, setDraftGroupName] = useState('');
  const [titleBlockWidth, setTitleBlockWidth] = useState(0);
  const pendingSaveRef = useRef(false);
  const blurCancelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const displayGroupName = optimisticGroupName ?? group?.name ?? GROUP_FALLBACK_NAME;

  useEffect(() => {
    return () => {
      if (blurCancelTimeoutRef.current) clearTimeout(blurCancelTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (optimisticGroupName != null && group?.name === optimisticGroupName) {
      setOptimisticGroupName(null);
    }
  }, [optimisticGroupName, group?.name]);

  const onTitleLayout = useCallback(
    (e: { nativeEvent: { layout: { width: number } } }) => {
      if (!isEditingGroupName) {
        setTitleBlockWidth(e.nativeEvent.layout.width);
      }
    },
    [isEditingGroupName],
  );

  const handleStartEditGroupName = useCallback(() => {
    setDraftGroupName(displayGroupName);
    setIsEditingGroupName(true);
  }, [displayGroupName]);

  const handleCancelEditGroupName = useCallback(() => {
    if (pendingSaveRef.current) return;
    setIsEditingGroupName(false);
    setDraftGroupName(displayGroupName);
  }, [displayGroupName]);

  const handleBlurGroupName = useCallback(() => {
    if (blurCancelTimeoutRef.current) {
      clearTimeout(blurCancelTimeoutRef.current);
      blurCancelTimeoutRef.current = null;
    }
    blurCancelTimeoutRef.current = setTimeout(() => {
      blurCancelTimeoutRef.current = null;
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        return;
      }
      handleCancelEditGroupName();
    }, GROUP_NAME_EDIT_BLUR_CANCEL_DELAY_MS);
  }, [handleCancelEditGroupName]);

  const handleGroupNameChange = useCallback((text: string) => {
    setDraftGroupName(sanitizeInput(text, true).slice(0, GROUP_NAME_MAX_LENGTH));
  }, []);

  const handleSaveGroupName = useCallback(async () => {
    const trimmed = sanitizeInput(draftGroupName, true).trim();
    if (trimmed.length < GROUP_NAME_MIN_LENGTH) {
      pendingSaveRef.current = false;
      await showAlert({ title: GROUP_NAME_EDIT.EDIT_LABEL, message: GROUP_NAME_EDIT.TOO_SHORT, type: 'default' });
      return;
    }
    if (trimmed.length > GROUP_NAME_MAX_LENGTH) {
      pendingSaveRef.current = false;
      await showAlert({ title: GROUP_NAME_EDIT.EDIT_LABEL, message: GROUP_NAME_EDIT.TOO_LONG, type: 'default' });
      return;
    }
    if (!group) {
      pendingSaveRef.current = false;
      return;
    }
    if (trimmed === (displayGroupName ?? '').trim()) {
      pendingSaveRef.current = false;
      if (blurCancelTimeoutRef.current) {
        clearTimeout(blurCancelTimeoutRef.current);
        blurCancelTimeoutRef.current = null;
      }
      setIsEditingGroupName(false);
      setDraftGroupName('');
      return;
    }
    if (blurCancelTimeoutRef.current) {
      clearTimeout(blurCancelTimeoutRef.current);
      blurCancelTimeoutRef.current = null;
    }
    const previousDashboard = queryClient.getQueryData<GroupDashboardResponse>(
      ['groups', 'dashboard', group.id],
    );
    setOptimisticGroupName(trimmed);
    queryClient.setQueryData<GroupDashboardResponse>(
      ['groups', 'dashboard', group.id],
      (old) => (old ? { ...old, name: trimmed } : old),
    );
    setIsEditingGroupName(false);
    setDraftGroupName('');
    try {
      await updateGroupName(group.id, trimmed, apiClient);
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      showSnackbar(GROUP_NAME_EDIT.SAVE_SUCCESS, 'success');
    } catch {
      setOptimisticGroupName(null);
      if (previousDashboard) {
        queryClient.setQueryData(['groups', 'dashboard', group.id], previousDashboard);
      }
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      await showAlert({
        title: GROUP_NAME_EDIT.EDIT_LABEL,
        message: GROUP_NAME_EDIT.UPDATE_FAILED,
        type: 'danger',
      });
    } finally {
      pendingSaveRef.current = false;
    }
  }, [draftGroupName, displayGroupName, group, apiClient, queryClient, showAlert, showSnackbar]);

  return {
    displayGroupName,
    isEditingGroupName,
    draftGroupName,
    titleBlockWidth,
    pendingSaveRef,
    onTitleLayout,
    handleStartEditGroupName,
    handleCancelEditGroupName,
    handleBlurGroupName,
    handleGroupNameChange,
    handleSaveGroupName,
  };
}
